import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Que al cambiar de pantalla se empiece por el principio.
 *
 * En una página web de toda la vida, pulsar un enlace te lleva arriba del
 * documento nuevo. En una aplicación de una sola página no pasa: el navegador
 * no carga nada, solo se cambia lo pintado, y **el desplazamiento se queda donde
 * estaba**. React Router no lo toca, y aquí no había ni una línea que lo hiciera.
 *
 * Se notaba justo donde más molesta: desde el pie de la portada —que está a
 * varias pantallas de scroll— los enlaces a Términos de Servicio y a Política de
 * Privacidad abrían el documento **por la mitad**. Para leer lo que estás
 * aceptando había que subir a mano hasta encontrar el principio.
 *
 * ## Dos casos en los que NO hay que subir, y por eso esto no es una línea
 *
 * **Volver atrás.** Si vienes de pulsar «atrás», lo que esperas es reaparecer
 * donde lo dejaste, no en lo alto de una lista que ya habías recorrido. React
 * Router llama `POP` a esa navegación, y entonces esto no hace nada: deja que
 * el navegador restaure la posición, que es su trabajo y lo hace bien.
 *
 * **Los enlaces a un ancla.** «Aprender Más» en la portada apunta a
 * `#features`, más abajo en la misma página. Subir arriba justo después sería
 * deshacer lo que la persona acaba de pedir.
 */
export default function IrArriba() {
    const { pathname, hash } = useLocation();
    const tipo = useNavigationType();

    useEffect(() => {
        if (tipo === 'POP' || hash) return;

        // `instant` y no `smooth`: esto no es un gesto que la persona haya
        // pedido ver, es el punto de partida de la pantalla nueva. Animar el
        // recorrido enseñaría de paso un contenido que ya no viene al caso.
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [pathname, hash, tipo]);

    return null;
}
